// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// ================================================================
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'iod_text_size';
const SIZES = [
  { label: 'S', value: 'small', scale: 0.85 },
  { label: 'M', value: 'medium', scale: 1.0 },
  { label: 'L', value: 'large', scale: 1.15 },
  { label: 'XL', value: 'xlarge', scale: 1.3 },
];

const STYLE_ID = 'iod-text-scale';

function applyScale(scale) {
  document.documentElement.style.setProperty('--text-scale', scale);
  // Set root font-size for rem-based sizing
  document.documentElement.style.fontSize = `${scale * 100}%`;

  // Inject/update a style tag that forces zoom-like scaling on the main content
  // This ensures ALL text (including px-based) gets scaled
  let styleEl = document.getElementById(STYLE_ID);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ID;
    document.head.appendChild(styleEl);
  }

  // Use CSS zoom on the main content area (not the sidebar) to scale everything
  // For Firefox (no zoom support), use transform as fallback
  styleEl.textContent = `
    .main-content {
      zoom: ${scale};
      -moz-transform: scale(${scale});
      -moz-transform-origin: top left;
    }
    @supports not (zoom: 1) {
      .main-content {
        transform: scale(${scale});
        transform-origin: top left;
        width: ${100 / scale}%;
      }
    }
    /* Keep sidebar at normal size */
    .sidebar { zoom: 1 !important; transform: none !important; }
  `;
}

export default function useTextSize() {
  const [size, setSize] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || 'medium';
  });

  useEffect(() => {
    const sizeObj = SIZES.find(s => s.value === size) || SIZES[1];
    applyScale(sizeObj.scale);
    localStorage.setItem(STORAGE_KEY, size);
  }, [size]);

  const cycleSize = useCallback(() => {
    const idx = SIZES.findIndex(s => s.value === size);
    const next = SIZES[(idx + 1) % SIZES.length];
    setSize(next.value);
  }, [size]);

  return { size, setSize, cycleSize, SIZES };
}
